'use client';

import { useState, useEffect } from 'react';
import { cn } from '@/utils/cn';
import { IntroSection } from './IntroSection';
import { RecentSection } from './RecentSection';
import { SupportSection } from './SupportSection';
import { User } from '@/utils/helpers';

const sections = [
  { id: 'intro', title: 'Introduction' },
  { id: 'updates', title: 'Updates' },
  { id: 'report', title: 'Report Issues' },
];

export function ChangelogPage({
  description,
  user,
}: {
  description: string;
  user: Partial<User> | null;
}) {
  const [activeSection, setActiveSection] = useState('intro');

  // Update active section based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100; // Add offset for better UX

      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="pb-16 bg-black [scroll-behavior:smooth]">
      <style jsx global>{`
        html {
          scroll-behavior: smooth;
        }
      `}</style>
      <div className="max-w-6xl px-4 py-8 mx-auto sm:px-6 sm:pt-12 flex flex-col md:flex-row gap-16">
        {/* Sidebar */}
        <div className="w-full md:sticky md:top-6 md:h-screen flex flex-col flex-1 md:border-r border-zinc-700 border-opacity-50 md:pr-16">
          <h1 className="text-3xl font-bold pb-6">Changelog</h1>
          <nav className="space-y-2 min-w-48">
            {sections.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className={cn(
                  'w-full text-left px-3 py-2 rounded-lg transition-colors block',
                  activeSection === section.id
                    ? 'bg-zinc-800 text-white'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                )}
              >
                {section.title}
              </a>
            ))}
          </nav>
        </div>

        {/* Main Content */}
        <div className="w-full max-w-4xl flex flex-col gap-8 md:gap-12 lg:gap-16 min-w-0">
          <IntroSection description={description} user={user} />
          <RecentSection />
          <SupportSection />
        </div>
      </div>
    </section>
  );
}
